import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';
import {
  Home,
  Upload,
  Settings,
  Key,
  LogOut,
  CreditCard,
  Cpu,
  ChevronRight,
  Menu,
  X,
  HardDrive,
} from 'lucide-react';
import { clsx } from 'clsx';

interface LayoutProps {
  children: React.ReactNode;
}

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: Home },
  { path: '/builds', label: 'Builds', icon: Upload },
  { path: '/stored-files', label: 'Stored Files', icon: HardDrive },
  { path: '/configs', label: 'Configs', icon: Settings },
  { path: '/credits', label: 'Credits', icon: CreditCard },
  { path: '/api-keys', label: 'API Keys', icon: Key },
];

export function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const SidebarContent = () => (
    <>
      {/* Logo section */}
      <div className="p-4 lg:p-6 border-b border-slate-800/50">
        <div className="flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3 group" onClick={() => setSidebarOpen(false)}>
            <div className="relative">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-glow">
                <Cpu className="w-5 h-5 text-white" />
              </div>
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 blur-lg opacity-50 group-hover:opacity-75 transition-opacity" />
            </div>
            <div>
              <span className="text-xl font-bold text-gradient tracking-tight">
                CipherBin
              </span>
              <div className="hidden sm:flex items-center gap-1.5 mt-0.5">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">
                  Cipher-Grade Protection
                </span>
              </div>
            </div>
          </Link>
          
          {/* Close button - visible only on mobile */}
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-lg hover:bg-slate-800/50 text-slate-400 hover:text-white transition-colors"
            aria-label="Close sidebar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 lg:p-4 space-y-1 overflow-y-auto">
        {navItems.map((item, index) => {
          const isActive = location.pathname.startsWith(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={clsx(
                'flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 group relative',
                'lg:opacity-0-init lg:animate-fade-in-up',
                isActive
                  ? 'bg-gradient-to-r from-cyan-500/10 to-purple-500/10 text-white border border-cyan-500/20'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
              )}
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              {/* Active indicator */}
              {isActive && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-full bg-gradient-to-b from-cyan-500 to-purple-500" />
              )}
              
              <item.icon className={clsx(
                'w-5 h-5 transition-colors flex-shrink-0',
                isActive ? 'text-cyan-400' : 'text-slate-500 group-hover:text-cyan-400'
              )} />
              
              <span className="font-medium flex-1">{item.label}</span>
              
              <ChevronRight className={clsx(
                'w-4 h-4 transition-all flex-shrink-0',
                isActive ? 'opacity-100 text-cyan-400' : 'opacity-0 group-hover:opacity-50'
              )} />
            </Link>
          );
        })}
      </nav>

      {/* User section */}
      <div className="p-3 lg:p-4 border-t border-slate-800/50">
        <div className="glass rounded-xl p-3 lg:p-4 mb-3">
          <div className="flex items-center gap-3">
            {/* Avatar with gradient border */}
            <div className="relative flex-shrink-0">
              <div className="w-10 h-10 lg:w-11 lg:h-11 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-500 p-[2px]">
                <div className="w-full h-full rounded-[10px] bg-slate-900 flex items-center justify-center">
                  <span className="text-base lg:text-lg font-bold text-gradient">
                    {user?.username?.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-slate-900" />
            </div>
            
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-white truncate text-sm lg:text-base">{user?.username}</p>
              <div className="flex items-center gap-1.5">
                <div className="w-1 h-1 rounded-full bg-purple-500" />
                <span className="text-xs font-mono text-purple-400">
                  {user?.credits} credits
                </span>
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-all duration-300 group"
        >
          <LogOut className="w-4 h-4 group-hover:rotate-12 transition-transform" />
          <span className="text-sm font-medium">Sign out</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Background effects */}
      <div className="bg-mesh" />
      <div className="bg-noise" />
      <div className="fixed inset-0 bg-grid pointer-events-none opacity-40" />

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-40 glass-strong border-b border-slate-800/50">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 flex items-center justify-center">
              <Cpu className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold text-gradient">CipherBin</span>
          </Link>
          
          <div className="flex items-center gap-3">
            {/* Credits badge */}
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/20">
              <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />
              <span className="text-xs font-mono text-purple-400">{user?.credits}</span>
            </div>
            
            {/* Hamburger button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg hover:bg-slate-800/50 text-slate-400 hover:text-white transition-colors"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      <div
        className={clsx(
          'lg:hidden fixed inset-0 z-50 transition-opacity duration-300',
          sidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
        
        {/* Sidebar panel */}
        <aside
          className={clsx(
            'absolute top-0 left-0 h-full w-72 max-w-[85vw] glass-strong border-r border-slate-800/50 flex flex-col transition-transform duration-300 ease-out',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          <SidebarContent />
        </aside>
      </div>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-72 glass-strong relative z-10 flex-col border-r border-slate-800/50 h-screen sticky top-0">
        <SidebarContent />
      </aside>

      {/* Main content */}
      <main className="flex-1 relative z-10 overflow-auto min-h-0">
        <div className="p-4 lg:p-8 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
